import React, { useState, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowRight,
  Bot,
  X,
  Flag,
  Save,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import { Agent } from '../types';
import { useAgentInteractions } from '../hooks/useAgentInteractions';
import { useAuth } from '../hooks/useAuth';
import { AGENT_PATCH_NOTES, ENABLE_VERSION_TRACKING, VERSION_TRACKING_BASELINE_DATE } from '../config/patchNotesData';
import { API_BASE, authFetch } from '../pages/admin/constants';
import { SCOUT_CONTAINER } from './scoutLayout';
import { trackHomeEvent } from '../services/homeTelemetry';
import './MyAgentsSection.css';

interface MyAgentsSectionProps {
  agents: Agent[];
  onStartConversation: (agentId: string) => void;
  /** Reload agents after a super-admin changes an agent's home-screen notice,
   *  so the NEW/UPDATED badge reflects the new global state immediately. */
  onNoticeSaved?: () => void;
}

// UniFirst brand palette (from Brand Standards Manual / PMS swatches).
// Each agent gets a unique color pair for its icon container gradient,
// drawn from the official primary, secondary, and tertiary palette.
const UNIFIRST_PALETTE = {
  primary:     { from: '#00B194', to: '#007161' }, // PMS 339 → Deep Green (PMS 3298)
  teal:        { from: '#007672', to: '#005a4d' }, // PMS 7718 Teal
  olive:       { from: '#A8AD00', to: '#7a7e00' }, // PMS 383 Olive
  lime:        { from: '#CEDC00', to: '#A8AD00' }, // PMS 381 Bright Lime → Olive
  orange:      { from: '#DAAA00', to: '#b08900' }, // PMS 110 Burnt Orange
  yellow:      { from: '#FFB81C', to: '#DAAA00' }, // PMS 1235 Yellow → Burnt Orange
  coolGray:    { from: '#63666A', to: '#4a4d50' }, // Cool Gray 10
  slateGray:   { from: '#97999B', to: '#63666A' }, // Cool Gray 7 → Cool Gray 10
  crimson:     { from: '#5a3a3b', to: '#3F2021' }, // Dark Crimson (PMS 4975) lightened start
};

const AGENT_ICON_CONFIG: Record<string, {
  image: string;
  fallbackIcon: React.ComponentType<{ className?: string }>;
  colors: { from: string; to: string };
  noBackground?: boolean;
}> = {
  'aldo':              { image: '/assets/logos/aldo-no-back-128.png',         fallbackIcon: Bot, colors: UNIFIRST_PALETTE.primary },
  'orientation':       { image: '/assets/logos/Aldoorientation-128.png',      fallbackIcon: Bot, colors: UNIFIRST_PALETTE.lime },
  'hrpolicy':          { image: '/assets/logos/Aldohrpolicy-128.png',         fallbackIcon: Bot, colors: UNIFIRST_PALETTE.teal },
  // Uses the default UNF mark (no dedicated PWMS icon yet) — see AA-220 rebrand follow-up.
  'pwms-support':      { image: '/assets/logos/aldo-no-back-128.png',         fallbackIcon: Bot, colors: UNIFIRST_PALETTE.coolGray },
  'product':           { image: '/assets/logos/aldo__product-128.png',        fallbackIcon: Bot, colors: UNIFIRST_PALETTE.orange },
  'direct_embroidery': { image: '/assets/logos/aldo-embroidery.png',          fallbackIcon: Bot, colors: UNIFIRST_PALETTE.crimson },
  'unifirst_docs':     { image: '/assets/logos/aldounifirstdocs2-128.png',    fallbackIcon: Bot, colors: UNIFIRST_PALETTE.olive },
  'service_desk':      { image: '/assets/logos/aldo-service-desk-128.png',    fallbackIcon: Bot, colors: UNIFIRST_PALETTE.yellow },
  'abs-assistant':     { image: '/assets/logos/aldo-no-back-128.png',         fallbackIcon: Bot, colors: UNIFIRST_PALETTE.teal },
};

// Agent Icon Component with image fallback
const AgentIconImage: React.FC<{
  agentId: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}> = ({ agentId, size = 'md', className = '' }) => {
  const [imageError, setImageError] = useState(false);
  const iconConfig = AGENT_ICON_CONFIG[agentId] || AGENT_ICON_CONFIG['aldo'];
  const FallbackIcon = iconConfig.fallbackIcon;
  
  const sizeClasses = {
    sm: 'w-5 h-5',
    md: 'w-8 h-8',
    lg: 'w-14 h-14'
  };
  
  if (imageError) {
    return <FallbackIcon className={`${sizeClasses[size]} text-white ${className}`} />;
  }
  
  return (
    <img
      src={iconConfig.image}
      alt=""
      className={`${sizeClasses[size]} object-contain ${className}`}
      onError={() => setImageError(true)}
    />
  );
};

// The agent's coloured tile, sized for the card (md) or the version-history modal header (sm).
const AgentGlyph: React.FC<{ agentId: string; size?: 'sm' | 'md' }> = ({ agentId, size = 'md' }) => {
  const iconConfig = AGENT_ICON_CONFIG[agentId] || {
    image: '/assets/logos/aldo-no-back-128.png',
    fallbackIcon: Bot,
    colors: UNIFIRST_PALETTE.slateGray,
  };
  const tile = size === 'sm' ? 'w-8 h-8 rounded-lg' : 'w-12 h-12 rounded-xl';
  // Several source PNGs are dark or mid-grey and vanish on similarly dark tiles (IRIS, PWMS
  // Support, First Aid & Safety), so render every image as a white silhouette, as the previous
  // tiles did. Skipped for a "no background" tile, which is already light.
  const silhouette = iconConfig.noBackground
    ? ''
    : '[&_img]:[filter:brightness(0)_invert(1)_drop-shadow(0_1px_2px_rgba(0,0,0,0.25))]';
  return (
    <span
      aria-hidden
      className={`${tile} ${silhouette} shrink-0 flex items-center justify-center shadow-sm`}
      style={iconConfig.noBackground ? undefined : { background: `linear-gradient(to bottom right, ${iconConfig.colors.from}, ${iconConfig.colors.to})` }}
    >
      <AgentIconImage agentId={agentId} size={size === 'sm' ? 'sm' : 'md'} />
    </span>
  );
};

// Mini Patch Notes Modal for individual agents
// Prefers versionHistory from agents.json (single source of truth editable via Management UI),
// falls back to hardcoded AGENT_PATCH_NOTES for agents that haven't been migrated yet.
const AgentPatchNotesModal: React.FC<{
  agent: Agent;
  isOpen: boolean;
  onClose: () => void;
  /** Reload agents after the embedded super-admin notice control saves. */
  onNoticeSaved?: () => void;
}> = ({ agent, isOpen, onClose, onNoticeSaved }) => {
  // Build unified patch notes: prefer agent.versionHistory from agents.json,
  // then fall back to hardcoded AGENT_PATCH_NOTES
  const patchNotes = useMemo(() => {
    if (agent.versionHistory && agent.versionHistory.length > 0) {
      // Map versionHistory from agents.json → PatchNoteEntry shape
      return agent.versionHistory
        .slice()
        .reverse()  // newest first
        .map(entry => ({
          version: entry.version,
          date: entry.date,
          highlights: entry.notes
            ? entry.notes.split(/[;,\n]/).map(s => s.trim()).filter(Boolean)
            : [`Version ${entry.version} release`],
        }));
    }
    return AGENT_PATCH_NOTES[agent.id] || [];
  }, [agent.id, agent.versionHistory]);
  
  // Detect dark mode - check multiple sources including data-theme attribute
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  React.useEffect(() => {
    const checkDarkMode = () => {
      const root = document.documentElement;
      const hasDarkTheme = root.getAttribute('data-theme') === 'dark';
      const hasDarkClass = root.classList.contains('dark') || 
                          document.body.classList.contains('dark') ||
                          document.querySelector('.dark') !== null;
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setIsDarkMode(hasDarkTheme || hasDarkClass || prefersDark);
    };
    
    checkDarkMode();
    
    // Listen for changes to both class and data-theme attribute
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, { 
      attributes: true, 
      attributeFilter: ['class', 'data-theme'] 
    });
    
    return () => observer.disconnect();
  }, [isOpen]);
  
  if (!isOpen) return null;
  
  return createPortal(
    <div className="agent-patch-modal-overlay" onClick={onClose}>
      <div 
        className="agent-patch-modal" 
        onClick={e => e.stopPropagation()}
        style={isDarkMode ? { 
          background: '#1f2124', 
          border: '1px solid #3d4045' 
        } : undefined}
      >
        <div className="agent-patch-modal-header" style={isDarkMode ? { borderBottomColor: '#3d4045' } : undefined}>
          <div className="flex items-center gap-3">
            <AgentGlyph agentId={agent.id} size="sm" />
            <div>
              <h3 className="font-semibold" style={{ color: isDarkMode ? '#f8f9fa' : '#1f2124' }}>{agent.name}</h3>
              <p className="text-xs" style={{ color: isDarkMode ? '#9da2a8' : '#6f747b' }}>Version History</p>
            </div>
          </div>
          <button onClick={onClose} className="agent-patch-close" style={isDarkMode ? { color: '#9da2a8' } : undefined}>
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="agent-patch-modal-content">
          {/* Super-admin only (this whole modal is gated to super-admins): set the
              home-screen NEW/UPDATED badge for all users, as of a date. */}
          <AgentNoticeControls agent={agent} isDarkMode={isDarkMode} onSaved={onNoticeSaved} />
          {patchNotes.length > 0 ? (
            <div className="space-y-4">
              {patchNotes.map((note, idx) => (
                <div 
                  key={idx} 
                  className="agent-patch-entry"
                  style={isDarkMode ? { background: '#2b2e32' } : undefined}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="agent-patch-version">{note.version}</span>
                    <span className="agent-patch-date" style={isDarkMode ? { color: '#9da2a8' } : undefined}>{note.date}</span>
                  </div>
                  <ul className="agent-patch-list">
                    {note.highlights.map((highlight, hIdx) => (
                      <li key={hIdx} style={isDarkMode ? { color: '#cfd2d6' } : undefined}>{highlight}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-center py-4" style={{ color: isDarkMode ? '#9da2a8' : '#6f747b' }}>
              No version history available yet.
            </p>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

// Inline super-admin control embedded at the top of the version modal: set this
// agent as NEW or UPDATED "as of" a date, globally for all users. Persists via
// the narrow whats-new endpoint (agents.json + best-effort graph dual-write);
// each user then sees the badge until they next open the agent after that date.
// The version modal is itself super-admin-only, so no extra role gate is needed.
const AgentNoticeControls: React.FC<{
  agent: Agent;
  isDarkMode: boolean;
  onSaved?: () => void;
}> = ({ agent, isDarkMode, onSaved }) => {
  const [kind, setKind] = useState<'new' | 'updated'>(agent.updateNotice?.kind === 'new' ? 'new' : 'updated');
  const [date, setDate] = useState<string>(agent.updateNotice?.date || new Date().toISOString().split('T')[0]);
  const [saving, setSaving] = useState<'set' | 'clear' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const active = !!agent.updateNotice?.enabled;

  const submit = async (enabled: boolean) => {
    setSaving(enabled ? 'set' : 'clear');
    setError(null);
    setSuccess(null);
    try {
      const body = {
        updateNotice: {
          enabled,
          kind,
          date,
          // Preserve any existing "What's New" blurb; this control only toggles
          // the badge type/date, it doesn't touch the intro-message text.
          summary: agent.updateNotice?.summary || '',
        },
      };
      const res = await authFetch(`${API_BASE}/admin/registry/${agent.id}/whats-new`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Save failed: ${res.status}`);
      }
      setSuccess(enabled
        ? `Marked ${kind.toUpperCase()} as of ${date} for all users.`
        : 'Home-screen notice cleared for all users.');
      onSaved?.();
      setTimeout(() => setSuccess(null), 4000);
    } catch (e: any) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(null);
    }
  };

  const labelColor = isDarkMode ? '#9da2a8' : '#6f747b';
  const inputBg = isDarkMode ? '#2b2e32' : '#fff';
  const inputBorder = isDarkMode ? '#3d4045' : '#cfd2d6';
  const inputText = isDarkMode ? '#e3e5e8' : '#1f2124';

  return (
    <div
      style={{
        padding: '12px', borderRadius: 8, marginBottom: 16,
        border: `1px solid ${isDarkMode ? '#3d4045' : '#e3e5e8'}`,
        background: isDarkMode ? '#17181a' : '#f8f9fa',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <Flag size={14} style={{ color: '#00B194' }} />
        <span style={{ fontSize: '0.8rem', fontWeight: 700, color: inputText, textTransform: 'uppercase', letterSpacing: '0.02em' }}>
          Home-screen notice
        </span>
        {active && (
          <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#047857', background: 'rgba(0,177,148,0.12)', padding: '1px 6px', borderRadius: 9999 }}>
            {(agent.updateNotice?.kind === 'new' ? 'NEW' : 'UPDATED')} active
          </span>
        )}
      </div>
      <p style={{ fontSize: '0.8rem', color: labelColor, margin: '0 0 10px' }}>
        Shows a badge and floats this agent to the top of the home screen for <strong>all users</strong>,
        until each user next opens it after the date below.
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        {(['new', 'updated'] as const).map(k => (
          <button
            key={k}
            onClick={() => setKind(k)}
            style={{
              flex: 1, padding: '7px 10px', fontSize: '0.8rem', fontWeight: 600,
              borderRadius: 6, cursor: 'pointer', textTransform: 'uppercase',
              border: kind === k ? '2px solid #00B194' : `1px solid ${inputBorder}`,
              background: kind === k ? 'rgba(0,177,148,0.10)' : inputBg,
              color: kind === k ? '#047857' : inputText,
            }}
          >
            {k}
          </button>
        ))}
      </div>

      <label style={{ fontSize: '0.75rem', color: labelColor, display: 'block', marginBottom: 4 }}>As of date</label>
      <input
        type="date"
        value={date}
        onChange={e => setDate(e.target.value)}
        style={{ width: '100%', padding: '6px 8px', fontSize: '0.85rem', border: `1px solid ${inputBorder}`, borderRadius: 6, background: inputBg, color: inputText }}
      />

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button
          onClick={() => submit(true)}
          disabled={saving !== null}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '7px 12px', fontSize: '0.85rem', fontWeight: 600,
            background: '#00B194', color: '#fff', border: 'none', borderRadius: 6,
            cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1,
          }}
        >
          <Save size={15} /> {saving === 'set' ? 'Saving…' : `Set ${kind.toUpperCase()}`}
        </button>
        <button
          onClick={() => submit(false)}
          disabled={saving !== null || !active}
          title={active ? 'Clear the notice for all users' : 'No active notice'}
          style={{
            padding: '7px 12px', fontSize: '0.85rem', fontWeight: 600,
            background: inputBg, color: active ? '#b91c1c' : labelColor,
            border: `1px solid ${active ? '#fecaca' : inputBorder}`, borderRadius: 6,
            cursor: (saving || !active) ? 'default' : 'pointer', opacity: (saving || !active) ? 0.6 : 1,
          }}
        >
          {saving === 'clear' ? 'Clearing…' : 'Clear'}
        </button>
      </div>

      {success && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, color: '#047857', fontSize: '0.8rem' }}>
          <CheckCircle size={15} /> {success}
        </div>
      )}
      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, color: '#b91c1c', fontSize: '0.8rem' }}>
          <AlertTriangle size={15} /> {error}
        </div>
      )}
    </div>
  );
};

// Agent Card Component — a card with substance rather than an app icon floating in space: what the
// agent is for, its business area, and one clear action. Primary (name), secondary (description) and
// tertiary (tag, version) information each get their own weight.
const AgentCard: React.FC<{
  agent: Agent;
  /** 1-based place in the grid, recorded with the click. */
  position: number;
  onStartConversation: (agentId: string) => void;
  isNew: boolean;
  isUpdated: boolean;
  onMarkSeen: (agentId: string, version: string, interactionType?: 'conversation' | 'question_click' | 'patch_notes_view') => void;
  onNoticeSaved?: () => void;
}> = ({ agent, position, onStartConversation, isNew, isUpdated, onMarkSeen, onNoticeSaved }) => {
  const [showPatchNotes, setShowPatchNotes] = useState(false);
  const { isSuperAdmin } = useAuth();

  const displayVersion = agent.version || '1.0.0';
  // ONLY super-admins see the version chit and its modal (which also hosts the NEW/UPDATED
  // home-screen notice control). Everyone else gets a clean card with no version badge.
  const canViewVersion = isSuperAdmin();

  const handleStartConversation = () => {
    trackHomeEvent({ name: 'HomeAgentCardClicked', properties: {
      agentId: agent.id,
      category: agent.category || 'none',
      badge: isNew ? 'new' : isUpdated ? 'updated' : 'none',
      position,
    } });
    onMarkSeen(agent.id, displayVersion, 'conversation');
    onStartConversation(agent.id);
  };

  // The version history modal is a super-admin management surface, not an end-user "what's new"
  // view, so opening it must NOT mark the agent as seen.
  const handleViewPatchNotes = (event: React.MouseEvent) => {
    event.stopPropagation();
    setShowPatchNotes(true);
  };

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={handleStartConversation}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleStartConversation();
          }
        }}
        aria-label={`Start a conversation with ${agent.name}`}
        className={`group relative h-full flex flex-col gap-3 p-4 rounded-2xl cursor-pointer text-left
          bg-scout-surface dark:bg-scout-surface-dark border shadow-scout-card
          transition-all duration-200 hover:-translate-y-0.5 hover:shadow-scout-card-hover hover:border-brand-400
          focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500
          ${isNew || isUpdated ? 'border-brand-400 dark:border-brand-600' : 'border-scout-line dark:border-scout-line-dark'}`}
      >
        {(isNew || isUpdated) && (
          <span className="absolute top-3.5 right-3.5 px-1.5 py-px rounded-md text-[10px] font-bold uppercase tracking-wide text-white bg-brand-500">
            {isNew ? 'New' : 'Updated'}
          </span>
        )}

        <div className="flex items-start gap-3">
          <AgentGlyph agentId={agent.id} />
          <div className="min-w-0 flex-1">
            <h3 className={`text-[15px] font-semibold leading-snug text-scout-ink dark:text-white ${isNew || isUpdated ? 'pr-16' : ''}`}>
              {agent.name}
            </h3>
            <p className="mt-1 text-[13px] leading-relaxed text-scout-ink-2 dark:text-gray-400 line-clamp-2">
              {agent.description || 'AI assistant for specialized tasks.'}
            </p>
          </div>
        </div>

        <div className="mt-auto flex items-center justify-between gap-2 min-w-0">
          {agent.category ? (
            <span className="min-w-0 truncate whitespace-nowrap px-2 py-0.5 rounded-md text-[11px] font-medium text-brand-800 dark:text-brand-300 bg-brand-50 dark:bg-brand-900/40">
              {agent.category}
            </span>
          ) : <span />}
          <span className="flex items-center gap-2 shrink-0">
            {canViewVersion && (
              <button
                type="button"
                onClick={handleViewPatchNotes}
                title="Version history & home-screen notice"
                className="px-1.5 py-px rounded-md text-[10px] font-medium tabular-nums text-scout-ink-3 border border-scout-line dark:border-scout-line-dark hover:text-brand-700 hover:border-brand-400 transition-colors"
              >
                v{displayVersion}
              </button>
            )}
          <span
            aria-hidden
            className="flex items-center justify-center w-7 h-7 rounded-full border border-scout-line dark:border-scout-line-dark text-scout-ink-3 transition-colors group-hover:bg-brand-500 group-hover:border-brand-500 group-hover:text-white"
          >
            <ArrowRight className="w-3.5 h-3.5" />
          </span>
          </span>
        </div>
      </div>

      <AgentPatchNotesModal
        agent={agent}
        isOpen={showPatchNotes}
        onClose={() => setShowPatchNotes(false)}
        onNoticeSaved={onNoticeSaved}
      />
    </>
  );
};

// Main My Agents Section
const MyAgentsSection: React.FC<MyAgentsSectionProps> = ({
  agents,
  onStartConversation,
  onNoticeSaved
}) => {
  // Use the hook for tracking agent interactions (syncs with backend)
  const { 
    interactions, 
    recordInteraction, 
    hasSeenAgent, 
    getLastSeenVersion 
  } = useAgentInteractions();
  
  // Mark agent as seen callback - now uses hook which syncs to API (only when tracking enabled)
  const handleMarkSeen = useCallback((agentId: string, version: string, interactionType: 'conversation' | 'question_click' | 'patch_notes_view' = 'conversation') => {
    if (ENABLE_VERSION_TRACKING) {
      recordInteraction(agentId, version, interactionType);
    }
  }, [recordInteraction]);
  
  // Which manual notice badge (if any) is still active for THIS user. A super-
  // admin sets `updateNotice` (kind = 'new' | 'updated', with an "as of" date)
  // globally for all users; each user then sees the badge until they next open
  // the agent on a day after the notice date. Independent of version tracking.
  const activeNoticeKind = useCallback((agent: Agent): 'new' | 'updated' | null => {
    const notice = agent.updateNotice;
    if (!notice || !notice.enabled) return null;
    const kind: 'new' | 'updated' = notice.kind === 'new' ? 'new' : 'updated';
    const last = interactions[agent.id]?.lastInteractionDate;
    if (!notice.date) return last ? null : kind; // no date → show until any interaction
    if (!last) return kind;
    // Inclusive of the whole notice day so a same-day notice still shows; it
    // clears once the user opens the agent on a later day.
    return new Date(last).getTime() <= new Date(`${notice.date}T23:59:59`).getTime() ? kind : null;
  }, [interactions]);

  // Filter out hidden agents and sort with new/updated first (only when tracking enabled)
  const visibleAgents = useMemo(() => {
    return agents
      .filter(agent => !agent.hidden)
      .map(agent => {
        // Manual update notice works regardless of the version-tracking flag.
        const noticeKind = activeNoticeKind(agent);
        const noticeNew = noticeKind === 'new';
        const noticeUpdated = noticeKind === 'updated';
        if (!ENABLE_VERSION_TRACKING) {
          return { agent, isNew: noticeNew, isUpdated: noticeUpdated };
        }

        // "NEW" is keyed off releaseDate (when the agent first existed),
        // NOT lastUpdated. An agent released long before the baseline is not
        // new even if it was recently updated — that's an UPDATE, not a new
        // agent. Without this guard, refreshing lastUpdated on an existing
        // agent (e.g. content/version bump) would mark it as NEW for every
        // user who has not yet opened it, which is wrong.
        const releasedAfterBaseline = agent.releaseDate
          ? agent.releaseDate > VERSION_TRACKING_BASELINE_DATE
          : false;
        const updatedAfterBaseline = agent.lastUpdated
          ? agent.lastUpdated > VERSION_TRACKING_BASELINE_DATE
          : false;

        if (!releasedAfterBaseline && !updatedAfterBaseline) {
          return { agent, isNew: noticeNew, isUpdated: noticeUpdated };
        }

        const currentVersion = agent.version || '0.0.1';
        const hasBeenSeen = hasSeenAgent(agent.id);
        const lastSeenVersion = getLastSeenVersion(agent.id);

        // NEW: a manual "new" notice is active for this user, OR the agent
        // itself is new since baseline AND the user has not opened it.
        const isNew: boolean = noticeNew || (releasedAfterBaseline && !hasBeenSeen);
        // UPDATED: agent existed before baseline (or was already seen), was
        // updated after baseline, and the user's last-seen version is stale —
        // OR a manual "updated" notice is active for this user.
        const isUpdated: boolean = !isNew
          && (noticeUpdated
            || (updatedAfterBaseline
              && hasBeenSeen
              && !!lastSeenVersion
              && lastSeenVersion !== currentVersion));
        
        return { agent, isNew, isUpdated };
      })
      .sort((a, b) => {
        // Priority: UPDATED > NEW > normal (only when tracking enabled)
        if (!ENABLE_VERSION_TRACKING) return 0;
        const aScore = a.isUpdated ? 2 : (a.isNew ? 1 : 0);
        const bScore = b.isUpdated ? 2 : (b.isNew ? 1 : 0);
        return bScore - aScore;
      });
  }, [agents, interactions, hasSeenAgent, getLastSeenVersion, activeNoticeKind]);
  
  if (visibleAgents.length === 0) {
    return null;
  }
  
  return (
    <section aria-labelledby="my-agents-heading" className={`${SCOUT_CONTAINER} pb-8`}>
      <div className="flex items-baseline justify-between mb-3">
        <h2 id="my-agents-heading" className="text-lg font-semibold text-scout-ink dark:text-white">
          My agents
        </h2>
        <span className="text-xs text-scout-ink-3 tabular-nums">
          {visibleAgents.length} available
        </span>
      </div>

      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
        {visibleAgents.map(({ agent, isNew, isUpdated }, index) => (
          <AgentCard
            key={agent.id}
            agent={agent}
            position={index + 1}
            onStartConversation={onStartConversation}
            isNew={isNew}
            isUpdated={isUpdated}
            onMarkSeen={handleMarkSeen}
            onNoticeSaved={onNoticeSaved}
          />
        ))}
      </div>
    </section>
  );
};

export default MyAgentsSection;
